import { useEffect, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { Landing } from "./pages/Landing";
import { BuildCampaign } from "./pages/BuildCampaign";
import { OrderStatus } from "./pages/OrderStatus";
import { Privacy } from "./pages/Privacy";
import { Terms } from "./pages/Terms";
import { Refunds } from "./pages/Refunds";
import { NotFound } from "./pages/NotFound";
import { getConfig, type AppConfig } from "./lib/api";

/** React Router's <Link> doesn't perform the browser's native page-scroll
 * behavior: it neither jumps to an on-page anchor (e.g. "/#pricing") nor
 * resets scroll position on an ordinary route change (e.g. clicking the
 * logo back to "/" while scrolled down elsewhere). Handle both manually
 * whenever the route changes. */
function ScrollToHash() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [pathname, hash]);

  return null;
}

export function App() {
  const [config, setConfig] = useState<AppConfig | null>(null);

  useEffect(() => {
    getConfig()
      .then(setConfig)
      .catch(() => setConfig(null));
  }, []);

  return (
    <>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <ScrollToHash />
      <Header />
      <main id="main">
        <Routes>
          <Route path="/" element={<Landing config={config} />} />
          <Route path="/build" element={<BuildCampaign config={config} />} />
          <Route path="/order/:token" element={<OrderStatus />} />
          <Route
            path="/privacy"
            element={<Privacy supportEmail={config?.supportEmail} />}
          />
          <Route path="/terms" element={<Terms supportEmail={config?.supportEmail} />} />
          <Route
            path="/refunds"
            element={<Refunds supportEmail={config?.supportEmail} />}
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer supportEmail={config?.supportEmail} />
    </>
  );
}
